import React, { KeyboardEvent, MouseEventHandler, ReactChildren, ReactNode } from 'react';
import isEqual from 'react-fast-compare';
import isModifiedEvent from '@support/isModifiedEvent';
import { isDownHandler, isUpHandler } from '@support/keyboardHandler';
import isInViewport from '@support/isInViewport';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styles from './List.module.scss';
import BaseObject from '@models/BaseObject';

interface ListSection<Item> {
  title: string;
  order: number;
  items: Array<Item>;
}

interface RenderedItemProps {
  isInList: boolean;
  onMouseMove: MouseEventHandler;
  onClick: MouseEventHandler;
}

interface ListProps<Item> {
  items: Array<Item> | Array<ListSection<Item>>;
  active?: boolean;
  hotKeyPriority?: 1 | 2;
  skipFirstItemSelect?: boolean;
  reset?: boolean;
  sectioned?: boolean;
  transitionTimings?: number;
  itemTransitionClassName?: string;
  onActive?: (activeItem: Item) => void;
  onHotKey?: (selectedItem: Item, e: KeyboardEvent, section?: ListSection<Item> | null) => void;
  onSelect?: (selectedItem: Item, newTab: boolean | null, eventType: string) => void;
  renderSection?: (sectionProps: ListSection<Item> & { children: ReactChildren }) => ReactNode;
  renderItem: (item: Item, active: boolean, itemProps: RenderedItemProps) => ReactNode;
  defaultSelectedIdx?: number;
  defaultSelectedSectionIdx?: number;
  listContainerSelector?: string;
  doNotRespondToItemsUpdate?: boolean;
}

interface ListState {
  selected?: number;
  section?: string;
  order: Array<any>;
}

export default class List<T extends BaseObject> extends React.Component<ListProps<T>, ListState> {
  container = null;
  selected: React.RefObject<HTMLElement> = React.createRef();

  state = {
    selected: null,
    section: null,
    order: [],
  };

  constructor(props) {
    super(props);

    if (props.sectioned === true) {
      this.state.order = this.buildOrder(props.items);
    }
  }

  get hotkeyKey(): 'hotkey' | 'hotkey_1' | 'hotkey_2' {
    if (this.props.hotKeyPriority && this.props.hotKeyPriority > 0) {
      return `hotkey_${this.props.hotKeyPriority}`;
    }

    return 'hotkey';
  }

  componentDidMount() {
    this.container = document.querySelector(this.props.listContainerSelector || '#content');
    window.app.on(this.hotkeyKey, this.handleHotKey);

    if (this.props.defaultSelectedIdx) {
      let sectionIdx = this.props.defaultSelectedSectionIdx;

      if (typeof sectionIdx != 'number') {
        sectionIdx = undefined;
      }

      this.select(this.props.defaultSelectedIdx, sectionIdx);
    } else if (!this.props.skipFirstItemSelect) {
      this.selectFirst();
    }
  }

  componentWillUnmount() {
    window.app.off(this.hotkeyKey, this.handleHotKey);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevProps.items, this.props.items) && !this.props.doNotRespondToItemsUpdate) {
      this.setState(
        {
          order: this.buildOrder(this.props.items),
        },
        () => {
          if (this.props.reset === false) {
            return this.adjustSelect(prevProps, prevState);
          }

          this.selectFirst();
        },
      );
    }

    if (prevProps.skipFirstItemSelect && !this.props.skipFirstItemSelect) {
      this.selectFirst();
    }

    if (!prevProps.active && this.props.active && prevState.selected !== null) {
      this.props.onActive(this.activeItem);
    }
  }

  private adjustSelect(prevProps, prevState) {
    const { items, sectioned } = this.props;
    const prevItems = prevProps.items;

    if (sectioned) {
      return;
    }

    if (prevItems.length > items.length && prevState.selected === prevItems.length - 1) {
      return this.setState({ selected: items.length - 1 }, () => {
        if (this.props.onActive) {
          this.props.onActive(this.activeItem);
        }
      });
    }

    if (this.props.onActive) {
      this.props.onActive(this.activeItem);
    }
  }

  private buildOrder(sections) {
    const keyed = {};
    for (const [i, section] of sections.entries()) {
      keyed[section.order] = i;
    }

    let keys = Object.keys(keyed);
    keys.sort((a, b) => Number(a) - Number(b));
    const order = [];

    for (const key of keys) {
      order.push(keyed[key]);
    }

    return order;
  }

  handleStart = () => {
    return this.select(0);
  };

  handleHotKey = (e) => {
    const active = this.props.active ?? true;

    if (!active || window.__activeConfirm) {
      return;
    }

    if (e.defaultPrevented) {
      return;
    }

    if (isUpHandler(e)) {
      e.preventDefault();
      return this.modifySelect(-1);
    } else if (isDownHandler(e)) {
      e.preventDefault();
      return this.modifySelect(1);
    } else if (e.keyCode === 13) {
      e.preventDefault();
      return this.handleSelected(e);
    }

    const selected = this.activeItem;

    if (selected && this.props.onHotKey) {
      this.props.onHotKey(
        selected,
        e,
        this.props.sectioned && (this.props.items[this.state.section] as ListSection<T>),
      );
    }
  };

  private modifySection(section, modifier) {
    const { order } = this.state;

    const max = order.length - 1;
    section = section + modifier;

    if (section > max) {
      return max;
    } else if (section < 0) {
      return 0;
    }

    return section;
  }

  private modifySectionedSelect(modifier) {
    let { selected, section, order } = this.state;
    const { items: sections } = this.props;

    if (section === null) {
      section = order[0];
    }

    if (selected === null) {
      return this.select(0, section);
    }

    let items = (sections[section] as ListSection<T>).items;
    selected = selected + modifier;
    let max = items.length - 1;

    if (selected < 0 || selected > max) {
      let newSection = this.modifySection(section, selected < 0 ? -1 : 1);

      if (section === newSection) {
        if (selected < 0 || selected > (sections[section] as ListSection<T>).items.length - 1) {
          return;
        }
      } else {
        section = newSection;
      }

      if (selected < 0) {
        selected = (sections[section] as ListSection<T>).items.length - 1;
      } else if (selected > max) {
        selected = 0;
      }
    }

    return this.select(selected, section, true);
  }

  private modifySelect(modifier) {
    let { selected } = this.state;
    const { items, sectioned } = this.props;

    if (sectioned) {
      return this.modifySectionedSelect(modifier);
    }

    const max = items.length - 1;

    if (selected === null) {
      return this.select(0);
    }

    selected = selected + modifier;
    selected = Math.max(0, selected);

    if (selected > max) {
      selected = max;
    }

    return this.select(selected, null, true);
  }

  private handleSelect(i, section) {
    if (this.state.selected !== i || this.state.section !== section) {
      return this.select(i, section);
    }
  }

  private get activeItem() {
    const { selected, section } = this.state;
    const { items, sectioned = false } = this.props;

    if (sectioned) {
      if (selected === null) {
        return null;
      }

      return (items[section] as ListSection<T>)?.items[selected];
    }

    return items[selected] as T;
  }

  private selectFirst() {
    const { order, section } = this.state;
    const { sectioned = false } = this.props;

    if (sectioned) {
      return this.select(0, section || order[0]);
    }

    return this.select(0);
  }

  private handleSelected(e) {
    const activeItem = this.activeItem;

    if (!activeItem) {
      return this.selectFirst();
    }

    return this.selectItem(activeItem, isModifiedEvent(e), 'keyboard');
  }

  private handleItemClick(item, e) {
    if (e.defaultPrevented || isModifiedEvent(e)) {
      return;
    }

    const { target } = e;

    if (
      target.matches('input') ||
      target.matches('textarea') ||
      target.matches('button') ||
      target.isContentEditable
    ) {
      return;
    }

    e.preventDefault();

    return this.selectItem(item, null, 'click');
  }

  private selectItem(item, newTab, type) {
    const { onSelect } = this.props;

    if (!onSelect) {
      return;
    }

    return onSelect(item, newTab, type);
  }

  private select(selected, section = null, scroll = false) {
    return this.setState({ selected, section }, () => {
      if (this.props.onActive) {
        this.props.onActive(this.activeItem);
      }

      if (!scroll) {
        return;
      }

      this.scrollIntoView();
    });
  }

  private scrollIntoView() {
    if (!this.selected.current) {
      return;
    }

    if (this.state.selected === 0 && !this.state.section) {
      this.container.scrollTop = 0;
    }

    if (isInViewport(this.selected.current, this.container)) {
      return;
    }

    this.selected.current.scrollIntoView({ behavior: 'auto', block: 'center' });
  }

  private listWrapperComponent() {
    return this.props.transitionTimings ? TransitionGroup : React.Fragment;
  }

  private renderSection(section, key) {
    const Wrapper = this.listWrapperComponent();

    return this.props.renderSection({
      ...section,
      key,
      children: (
        <Wrapper>
          {section.items.map((item, i) => this.renderItem(item, i, item.id || i, key, { section }))}
        </Wrapper>
      ),
    });
  }

  private renderItem(item, idx, key, sectionKey = null, props = {}) {
    const { selected, section } = this.state;
    const { renderItem } = this.props;
    const active = section === sectionKey && selected === idx;
    let Wrapper = React.Fragment;
    let wrapperProps: { key: any; classNames?: string; timeout?: number } = { key };

    if (this.props.transitionTimings) {
      // @ts-ignore
      Wrapper = CSSTransition;
      wrapperProps = {
        key,
        classNames: this.props.itemTransitionClassName || '',
        timeout: this.props.transitionTimings || 0,
      };
    }

    return (
      <Wrapper {...wrapperProps}>
        <div className={styles.item}>
          <span className={styles.anchor} ref={active ? this.selected : null} />
          {renderItem(item, active, {
            ...props,
            isInList: true,
            onMouseMove: this.handleSelect.bind(this, idx, sectionKey),
            onClick: this.handleItemClick.bind(this, item),
          })}
        </div>
      </Wrapper>
    );
  }

  render() {
    const Wrapper = this.listWrapperComponent();

    if (this.props.sectioned) {
      return (
        <>
          {(this.props.items as Array<ListSection<T>>).map((section, i) =>
            this.renderSection(section, i),
          )}
        </>
      );
    }

    return (
      <Wrapper>
        {(this.props.items as Array<T>).map((item, i) => this.renderItem(item, i, item.id || i))}
      </Wrapper>
    );
  }
}
